import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MoreHorizontal, Send, Trash2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useConfirm } from "@/contexts/ConfirmContext";
import { Section } from "./_shared";

const ROLES = ["viewer", "creator", "manager"];

export default function TeamPanel({ brand }) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const { user: currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [seats, setSeats] = useState(null);
  const [invite, setInvite] = useState({ email: "", role: "creator" });
  const [editingMember, setEditingMember] = useState(null);
  const isOwner = currentUser && brand.owner_user_id === currentUser.user_id;

  const refresh = async () => {
    try {
      const [{ data: m }, { data: inv }, { data: s }] = await Promise.all([
        api.get(`/brands/${brand.id}/members`),
        api.get(`/brands/${brand.id}/invitations`).catch(() => ({ data: [] })),
        api.get(`/brands/${brand.id}/seat-usage`).catch(() => ({ data: null })),
      ]);
      setMembers(m);
      setInvitations(inv || []);
      setSeats(s);
    } catch { /* ignore */ }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [brand.id]);

  const doInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/brands/${brand.id}/invite`, invite);
      toast[data?.email_sent === false ? "warning" : "success"](
        data?.email_sent === false ? t("settings.invite_warn_no_mail") : t("settings.invite_success").replace("{email}", invite.email),
      );
      setInvite({ email: "", role: "creator" });
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || t("toasts.invite_failed")); }
  };

  const changeRole = async (userId, role) => {
    try {
      await api.put(`/brands/${brand.id}/members/${userId}/role`, { role });
      toast.success(t("settings.role_updated"));
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || t("settings.role_update_failed")); }
  };

  const removeMember = async (userId, name) => {
    const ok = await confirm({
      title: "Teamlid verwijderen?",
      body: t("settings.remove_member_confirm").replace("{name}", name),
      confirmLabel: "Verwijderen",
      tone: "danger",
    });
    if (!ok) return;
    try { await api.delete(`/brands/${brand.id}/members/${userId}`); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || t("settings.member_delete_failed")); }
  };

  const resendInvite = async (inviteId, email) => {
    try {
      const { data } = await api.post(`/brands/${brand.id}/invitations/${inviteId}/resend`);
      toast[data?.email_sent ? "success" : "warning"](
        data?.email_sent ? t("settings.invite_resent_success").replace("{email}", email) : t("settings.resend_mail_failed"),
      );
    } catch (err) { toast.error(err.response?.data?.detail || t("settings.resend_failed")); }
  };

  const withdrawInvite = async (inviteId, email) => {
    const ok = await confirm({
      title: "Uitnodiging intrekken?",
      body: t("settings.invite_withdraw_confirm").replace("{email}", email),
      confirmLabel: "Uitnodiging intrekken",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/brands/${brand.id}/invitations/${inviteId}`);
      toast.success(t("settings.invite_withdrawn").replace("{email}", email));
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || t("settings.invite_withdraw_failed")); }
  };

  return (
    <>
      <Section title={t("settings.team_title")} subtitle={t("settings.team_sub")} action={null}>
        {seats && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="seat-usage">
            <SeatBadge label="Managers" used={seats.counts.manager} cap={seats.caps.manager} />
            <SeatBadge label="Creators" used={seats.counts.creator} cap={seats.caps.creator} />
            <SeatBadge label="Viewers" used={seats.counts.viewer} cap={Infinity} />
          </div>
        )}

        <form onSubmit={doInvite} className="grid md:grid-cols-[1fr_160px_auto] gap-3 items-end">
          <div>
            <Label className="text-[13px]">{t("settings.team_invite_email")}</Label>
            <Input data-testid="invite-email" required type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="mt-1 h-11 bg-white border-gray-200" placeholder="person@company.com" />
          </div>
          <div>
            <Label className="text-[13px]">{t("settings.team_invite_role")}</Label>
            <select data-testid="invite-role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} className="bh-select mt-1 h-11 w-full bg-white border border-gray-200 rounded-md px-3 text-sm">
              {ROLES.map((r) => <option key={r} value={r}>{t(`common.role_${r}`)}</option>)}
            </select>
          </div>
          <Button type="submit" data-testid="invite-submit" className="h-11 rounded-md bg-slate-900 text-white hover:bg-slate-800">
            {t("settings.team_invite")}
          </Button>
        </form>

        <div className="divide-y divide-gray-100 bg-white border border-gray-100 rounded-xl mt-4">
          {members.map((m) => (
            <MemberRow
              key={m.user_id}
              member={m}
              isOwner={isOwner}
              onEdit={() => setEditingMember(m)}
              onRemove={() => removeMember(m.user_id, m.name || m.email)}
            />
          ))}
          {invitations.map((inv) => (
            <PendingInviteRow
              key={inv.id}
              invite={inv}
              onResend={() => resendInvite(inv.id, inv.email)}
              onWithdraw={() => withdrawInvite(inv.id, inv.email)}
            />
          ))}
          {members.length === 0 && invitations.length === 0 && (
            <div className="p-8 text-center text-gray-500 text-sm">{t("settings.team_empty")}</div>
          )}
        </div>
      </Section>

      {isOwner && (
        <section className="rounded-xl border border-gray-200 bg-white p-6 md:p-8 mt-6">
          <TransferOwnershipBlock brand={brand} members={members} />
        </section>
      )}

      {isOwner && <DangerZone brand={brand} />}

      {editingMember && (
        <ChangeRoleModal
          member={editingMember}
          canEdit={isOwner}
          onClose={() => setEditingMember(null)}
          onSave={async (newRole) => {
            await changeRole(editingMember.user_id, newRole);
            setEditingMember(null);
          }}
        />
      )}
    </>
  );
}

function SeatBadge({ label, used, cap }) {
  const unlimited = cap === Infinity;
  const over = !unlimited && used >= cap;
  return (
    <span
      data-testid={`seat-${label.toLowerCase()}`}
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 h-7 rounded-md border ${
        over ? "bg-red-50 text-red-700 border-red-200" : "bg-gray-50 text-gray-700 border-gray-200"
      }`}
    >
      <span className="text-slate-500">{label}:</span>
      <span className="tabular-nums">
        {used} / {unlimited ? "∞" : cap}
      </span>
    </span>
  );
}

function MemberRow({ member: m, isOwner, onEdit, onRemove }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isHim = m.role === "owner";
  return (
    <div className="flex items-center gap-3 py-3 px-4">
      {m.picture ? (
        <img
          src={m.picture.startsWith("/api") ? `${API.replace(/\/api$/, "")}${m.picture}` : m.picture}
          alt=""
          className="w-9 h-9 rounded-md object-cover"
        />
      ) : (
        <div className="w-9 h-9 rounded-md bg-gray-300 flex items-center justify-center text-sm text-white">
          {(m.name || m.email || "?").charAt(0).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate text-slate-900">{m.name || m.email}</div>
        <div className="text-xs text-gray-500 truncate">{m.email}</div>
      </div>
      <span
        data-testid={`role-badge-${m.user_id}`}
        className="text-xs font-medium px-2 py-1 rounded-md bg-gray-100 text-gray-700 capitalize"
      >
        {m.role}
      </span>
      {isOwner && !isHim && (
        <div className="relative">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            data-testid={`member-menu-${m.user_id}`}
            className="w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:text-slate-900 hover:bg-gray-100"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-9 z-10 w-44 bg-white border border-gray-200 rounded-md shadow-lg py-1"
              onMouseLeave={() => setMenuOpen(false)}
            >
              <button
                onClick={() => { setMenuOpen(false); onEdit(); }}
                data-testid={`change-role-${m.user_id}`}
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-gray-50"
              >
                Change role
              </button>
              <button
                onClick={() => { setMenuOpen(false); toast.info("User is already a member — use the invite form to re-invite another email."); }}
                className="w-full text-left px-3 py-2 text-sm text-slate-500 hover:bg-gray-50"
              >
                Resend invitation
              </button>
            </div>
          )}
        </div>
      )}
      {isOwner && !isHim && (
        <button
          onClick={onRemove}
          data-testid={`remove-member-${m.user_id}`}
          className="w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50"
          aria-label="Remove member"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function PendingInviteRow({ invite, onResend, onWithdraw }) {
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="flex items-center gap-3 py-3 px-4 bg-gray-50/50">
      <div className="w-9 h-9 rounded-md bg-gray-200 flex items-center justify-center text-gray-500">
        <Mail className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate text-slate-900">{invite.email}</div>
        <div className="text-xs text-gray-500">{t("settings.invite_pending")}</div>
      </div>
      <span className="text-xs font-medium px-2 py-1 rounded-md bg-gray-100 text-gray-700 capitalize">
        {invite.role}
      </span>
      <div className="relative">
        <button
          onClick={() => setMenuOpen((o) => !o)}
          data-testid={`invite-menu-${invite.id}`}
          className="w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:text-slate-900 hover:bg-gray-100"
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-9 z-10 w-48 bg-white border border-gray-200 rounded-md shadow-lg py-1" onMouseLeave={() => setMenuOpen(false)}>
            <button
              onClick={() => { setMenuOpen(false); onResend(); }}
              data-testid={`resend-invite-${invite.id}`}
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-gray-50"
            >
              {t("settings.invite_resend")}
            </button>
            <button
              onClick={() => { setMenuOpen(false); onWithdraw(); }}
              data-testid={`withdraw-invite-${invite.id}`}
              className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              {t("settings.invite_withdraw")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ChangeRoleModal({ member, canEdit, onClose, onSave }) {
  const [role, setRole] = useState(member.role);
  return (
    <div className="fixed inset-0 z-[120] bg-white/10 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="change-role-modal">
        <h3 className="text-lg font-medium text-slate-900">Change role</h3>
        <p className="text-sm text-slate-500 mt-1 mb-4">
          Update <strong>{member.name || member.email}</strong>'s role in this workspace.
        </p>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={!canEdit}
          data-testid="change-role-select"
          className="w-full h-10 bg-white border border-gray-200 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <div className="flex justify-end gap-2 mt-6">
          <Button onClick={onClose} className="h-10 rounded-md bg-gray-100 text-slate-900 hover:bg-gray-200">Cancel</Button>
          <Button
            data-testid="change-role-save"
            disabled={!canEdit || role === member.role}
            onClick={() => onSave(role)}
            className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:bg-gray-100 disabled:text-gray-400"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function TransferOwnershipBlock({ brand, members }) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const managers = members.filter((m) => m.role === "manager");
  const [selected, setSelected] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (managers.length === 0) setSelected("");
    else if (!managers.find((m) => m.user_id === selected)) setSelected(managers[0].user_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managers.length]);

  const transfer = async () => {
    const target = managers.find((m) => m.user_id === selected);
    if (!target) return;
    const name = target.name || target.email;
    const confirmMsg = t("settings.team_transfer_confirm")
      .replace("{name}", name)
      .replace("{brand}", brand.name);
    const ok = await confirm({
      title: "Eigenaarschap overdragen?",
      body: confirmMsg,
      confirmLabel: "Versturen",
      tone: "default",
    });
    if (!ok) return;
    setSending(true);
    try {
      const { data } = await api.post(`/brands/${brand.id}/transfer-ownership`, {
        new_owner_user_id: target.user_id,
      });
      if (data.email_sent) {
        toast.success(t("settings.team_transfer_sent").replace("{email}", data.to_email));
      } else {
        toast.warning(t("settings.team_transfer_sent_fail").replace("{email}", data.to_email), {
          duration: 7000,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("settings.team_transfer_failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="transfer-ownership-block">
      <h3 className="font-display text-xl font-medium text-[#1a1a1a]">
        {t("settings.team_transfer_title")}
      </h3>
      <p className="text-sm text-gray-500 mt-1 mb-4">{t("settings.team_transfer_sub")}</p>

      {managers.length === 0 ? (
        <div className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-lg px-4 py-5">
          {t("settings.team_transfer_no_managers")}
        </div>
      ) : (
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-[13px]">{t("settings.team_transfer_select_label")}</Label>
            <select
              data-testid="transfer-select"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="bh-select mt-1 h-11 w-full bg-white border border-gray-200 rounded-md px-3 text-sm focus:outline-none focus:border-[var(--bh-accent,#fe7f57)]"
            >
              {managers.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.name || m.email} — {m.email}
                </option>
              ))}
            </select>
          </div>
          <Button
            data-testid="transfer-submit"
            onClick={transfer}
            disabled={sending || !selected}
            className="h-11 rounded-lg bg-[#1a1a1a] text-white hover:bg-black/90 disabled:opacity-60 px-4"
          >
            <Send className="w-4 h-4 mr-2" strokeWidth={1.75} />
            {sending ? t("settings.team_transfer_sending") : t("settings.team_transfer_cta")}
          </Button>
        </div>
      )}
    </div>
  );
}

function DangerZone({ brand }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState(false);
  const canConfirm = confirmName.trim() === brand.name;

  const closeModal = () => { setOpen(false); setConfirmName(""); };

  const doDelete = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      await api.delete(`/brands/${brand.id}`, { data: { confirm_name: confirmName.trim() } });
      // Flush the cached active-brand so Dashboard immediately bounces to the
      // overview and doesn't try to refetch a brand that no longer exists.
      try { localStorage.removeItem("bh_active_brand"); } catch { /* noop */ }
      toast.success(t("settings.danger_delete_success").replace("{brand}", brand.name));
      navigate("/brands");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("settings.danger_delete_failed"));
    } finally { setBusy(false); }
  };

  return (
    <section className="rounded-xl border border-red-200 bg-white p-6 md:p-8 mt-6" data-testid="workspace-danger-zone">
      <h3 className="font-display text-xl font-medium text-red-600">{t("settings.danger_title")}</h3>
      <p className="text-sm text-gray-500 mt-1 mb-4">{t("settings.danger_sub")}</p>
      <Button
        data-testid="danger-delete-btn"
        onClick={() => setOpen(true)}
        className="h-11 rounded-lg bg-white text-red-600 border border-red-300 hover:bg-red-50 px-4"
      >
        {t("settings.danger_delete_cta")}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-[120] bg-white/10 backdrop-blur-md flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="danger-delete-modal"
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-xl font-medium text-[#1a1a1a]">
              {t("settings.danger_modal_title").replace("{brand}", brand.name)}
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              {t("settings.danger_modal_body")}
            </p>
            <p className="text-xs text-gray-500 mt-4 mb-1">
              {t("settings.danger_modal_type_hint")}
            </p>
            <div className="font-mono text-xs text-gray-400 mb-2">{brand.name}</div>
            <Input
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={brand.name}
              className="bg-white"
              autoFocus
              data-testid="danger-delete-confirm-input"
            />
            <div className="flex justify-end gap-2 mt-6">
              <Button
                onClick={closeModal}
                data-testid="danger-delete-cancel"
                className="h-10 rounded-md bg-white text-[#1a1a1a] border border-gray-200 hover:bg-gray-50"
              >
                {t("settings.danger_modal_cancel")}
              </Button>
              <Button
                onClick={doDelete}
                disabled={!canConfirm || busy}
                data-testid="danger-delete-confirm"
                className="h-10 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {busy ? t("settings.danger_modal_deleting") : t("settings.danger_modal_delete")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
